import React, { memo, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Spinner from '../../../global/Spinner';

const propTypes = {
  loadMore: PropTypes.func.isRequired,
  recordsLength: PropTypes.number.isRequired,
  shouldLoadMore: PropTypes.bool.isRequired,
  offset: PropTypes.number,
  indicesRendered: PropTypes.array,
  onMoreLoaded: PropTypes.func,
};

const PhotoGridScrollLoader = ({
  loadMore,
  recordsLength,
  shouldLoadMore,
  offset = 50,
  indicesRendered = [],
  onMoreLoaded = () => {},
}) => {
  const timesLoaded = useRef(0);
  const lastLoadedRecordsLength = useRef(null);
  const [loading, setLoading] = useState(false);

  const onLoadMore = async () => {
    if (
      // Avoid fetching the same records in a row.
      lastLoadedRecordsLength.current === recordsLength
    ) {
      // Will reset lastRecordsLoaded but will not load new records on that instance.
      // We might still run the risk of being tied up with dupped photos, but should be
      // a lower chance.
      lastLoadedRecordsLength.current = null;
      return;
    }

    setLoading(true);

    await loadMore();
    lastLoadedRecordsLength.current = recordsLength;
    timesLoaded.current += 1;
    onMoreLoaded(timesLoaded.current);
    setLoading(false);
  };

  useEffect(() => {
    const lastIndexRendered = indicesRendered[indicesRendered.length - 1];

    // If the offset provided is larger than the records length
    // fetch more records. Also solves for cases where the
    // records are not enough to produce a scrollbar.
    if (
      (offset > recordsLength ||
        lastIndexRendered + 1 + offset >= recordsLength) &&
      !loading &&
      shouldLoadMore
    ) {
      onLoadMore();
    }
  }, [shouldLoadMore, indicesRendered, recordsLength]);

  return (
    loading && (
      <div className="text-center py-3">
        <Spinner data-testid="photo-grid-scroll-loader-spinner" size="sm" />
      </div>
    )
  );
};

PhotoGridScrollLoader.propTypes = propTypes;

export default memo(PhotoGridScrollLoader);
